import Utils from "ui/helpers/Utils";
import {
	SCENE_FINAL_HEIGHT,
	SCENE_FINAL_WIDTH,
	SCENE_FRAMES,
	SCENE_GRID_INDENT,
	SCENE_SOURCE_HEIGHT,
	SCENE_SOURCE_WIDTH,
	WALL_HEIGHT_COEFFICIENT,
	SPRITES_COLUMNS,
	STATIC_URL
} from "ui/config";
import EventDispatcher from "ui/EventDispatcher";
import CanvasClipArea from "ui/canvas/CanvasClipArea";

/**
 * @type {string}
 */
export const EVENT_LOADED = 'loaded';

/**
 * Scene sprites model
 */
export default class SceneSprites extends EventDispatcher {
	/**
	 * Class constructor
	 */
	constructor() {
		super();
		let finWidth = SCENE_FINAL_WIDTH + (SCENE_GRID_INDENT << 1),
			finHeight = SCENE_FINAL_HEIGHT + (SCENE_GRID_INDENT << 1),
			halfWidth = Math.round(finWidth / 2),
			floorHalfHeight = Math.round(
				(SCENE_FINAL_HEIGHT - Math.round(SCENE_FINAL_HEIGHT * WALL_HEIGHT_COEFFICIENT)) / 2
			);

		/**
		 * @type {{
		 *  finWidth: number,
		 *  finHeight: number,
		 *  frames: Array<HTMLImageElement>,
		 *  srcClip: CanvasClipArea,
		 *  finClip: CanvasClipArea,
		 *  index: Array<number>,
		 *  framesLocation: string,
		 *  bestSizeFrame: number,
		 *  bestSizeImage: HTMLImageElement,
		 *  offsetLeft: number,
		 *  offsetTop: number
		 *  }}
		 * @private
		 */
		this._props = {
			finWidth,
			finHeight,
			frames: [],
			srcClip: new CanvasClipArea(SCENE_SOURCE_WIDTH, SCENE_SOURCE_HEIGHT),
			finClip: new CanvasClipArea(SCENE_FINAL_WIDTH, SCENE_FINAL_HEIGHT),
			index: [],
			framesLocation: '',
			bestSizeFrame: -1,
			bestSizeImage: null,
			offsetLeft: halfWidth,
			offsetTop: SCENE_GRID_INDENT + (SCENE_FINAL_HEIGHT - floorHalfHeight)
		};
	}

	/**
	 * Draws the specified frame into the canvas context
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number} frame
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @returns {SceneSprites}
	 */
	drawFrame(ctx, frame, offsetX = 0, offsetY = 0, scaleFactor = 1) {
		let props = this._props;
		if (props.frames.length === 0 || props.index.length <= frame) {
			return this;
		}
		let spriteIndex = props.index[frame],
			bestImg = null;

		if (scaleFactor > 1) {
			if (props.bestSizeFrame !== spriteIndex) {
				void this._loadFrameImage(spriteIndex);
			}

			if (props.bestSizeImage !== null) {
				bestImg = props.bestSizeImage;
			}
		}

		if (bestImg !== null) {
			ctx.drawImage(
				bestImg, 0, 0, SCENE_SOURCE_WIDTH, SCENE_SOURCE_HEIGHT,
				offsetX, offsetY, SCENE_FINAL_WIDTH * scaleFactor, SCENE_FINAL_HEIGHT * scaleFactor
			);
		} else {
			ctx.save();
			props.finClip.createBorderPath(ctx, offsetX, offsetY, scaleFactor);
			ctx.clip();
			ctx.drawImage(
				props.frames[spriteIndex],
				offsetX,
				offsetY,
				SCENE_FINAL_WIDTH * scaleFactor,
				SCENE_FINAL_HEIGHT * scaleFactor
			);
			ctx.restore();
		}
		return this;
	}

	/**
	 * Loads best size frame image
	 *
	 * @param {number} frame
	 * @returns {Promise<void>}
	 * @private
	 */
	async _loadFrameImage(frame) {
		if (this._props.framesLocation === '') {
			this._props.bestSizeImage = null;
			return;
		}

		if (this._props.bestSizeFrame === frame) {
			return;
		}

		this._props.bestSizeFrame = frame;
		this._props.bestSizeImage = null;
		await Utils.wait(2000);

		if (this._props.bestSizeFrame !== frame) {
			return;
		}

		this._props.bestSizeImage = await Utils.loadImage(
			STATIC_URL + '/data/' + this._props.framesLocation + '/frame_' + frame + '.png'
		);
		this.trigger(EVENT_LOADED);
	}

	/**
	 * Generates sprites for the specified canvas
	 *
	 * @param {Canvas} canvas
	 * @returns {Promise<void>}
	 */
	async generateForCanvas(canvas) {
		let layers = canvas.getLayers(),
			spriteImg = Utils.createCanvas('', SCENE_SOURCE_WIDTH, SCENE_SOURCE_HEIGHT),
			index = [],
			framesPromises = [],
			frameIndex = -1;

		for (let f = 0; f < SCENE_FRAMES; f++) {
			// check if at least one layer has keyframe in current frame
			let hasKeyframe = false;
			for (let layer of layers) {
				if (layer.getClosestKeyFrameAt(f) === f) {
					hasKeyframe = true;
					break;
				}
			}

			if (hasKeyframe || f === 0) {
				frameIndex++;
				let ctx = spriteImg.ctx;
				ctx.clearRect(0, 0, SCENE_SOURCE_WIDTH, SCENE_SOURCE_HEIGHT);
				let queue = [];
				for (let layer of layers) {
					queue.push(layer.getImagePromise(f));
				}
				ctx.save();
				this._props.srcClip.createBorderPath(ctx);
				ctx.clip();
				for (let layer of (await Promise.all(queue)).reverse()) {
					layer.drawFrame(spriteImg, f);
				}
				ctx.restore();

				framesPromises.push(
					Utils.loadImage(
						spriteImg.el.toDataURL('image/png')
					)
				);
			}

			index.push(frameIndex);
		}

		this._props.frames = await Promise.all(framesPromises);
		this._props.index = index;
		this.trigger(EVENT_LOADED);
	}

	/**
	 * Loads sprites from image
	 *
	 * @param {string} url
	 * @param {Array<number>} index
	 * @param {string} [framesLocation]
	 * @returns {Promise<void>}
	 */
	async loadImage(url, index, framesLocation = '') {
		let img = await Utils.loadImage(STATIC_URL + '/data/' + url);
		if (!/sprites@/.test(url)) {
			// old scene image size with 512 width
			// resize image
			img = await Utils.resizeImage(
				img,
				img.naturalWidth * (SCENE_FINAL_WIDTH / 512)
			);
		}

		this._props.frames = await this._splitImage(img, index);
		this._props.index = index;
		this._props.framesLocation = framesLocation;
		this.trigger(EVENT_LOADED);
	}

	/**
	 * Splits large image with all frames to small images, one for each frame
	 *
	 * @protected
	 * @param {HTMLImageElement} img
	 * @param {Array<number>} index
	 * @returns {Promise<Array<HTMLImageElement>>}
	 */
	async _splitImage(img, index) {
		let sprite = Utils.createCanvas('', SCENE_FINAL_WIDTH, SCENE_FINAL_HEIGHT),
			framesPromises = [];

		let totalFrames = Math.max(...index);

		for (let frameIndex = 0; frameIndex <= totalFrames; frameIndex++) {
			// find sprite position in the map
			let mapY = Math.floor(frameIndex / SPRITES_COLUMNS),
				mapX = frameIndex - (mapY * SPRITES_COLUMNS),
				x = (mapY % 2 === 0 ? 0 : this._props.offsetLeft) + mapX * this._props.finWidth,
				y = mapY * this._props.offsetTop;

			sprite.ctx.clearRect(0, 0, SCENE_FINAL_WIDTH, SCENE_FINAL_HEIGHT);
			sprite.ctx.drawImage(
				img,
				x + SCENE_GRID_INDENT,
				y + SCENE_GRID_INDENT,
				SCENE_SOURCE_WIDTH,
				SCENE_SOURCE_HEIGHT,
				0,
				0,
				SCENE_FINAL_WIDTH,
				SCENE_FINAL_HEIGHT
			);
			framesPromises.push(
				Utils.loadImage(
					sprite.el.toDataURL('image/png')
				)
			);
		}

		return await Promise.all(framesPromises);
	}
}
